/**
 * Facet Filters Component - Handles collection filtering functionality
 * Manages filter form state, URL updates, and AJAX product grid updates
 */

import { Component } from "./component-base.js";
import { ThemeBus } from "./events-bus.js";
import { EVENTS } from "./event-constants.js";

/**
 * Custom element for collection page filters with AJAX updates
 * @extends HTMLElement
 */

class ActiveFiltersComponent extends HTMLElement {
  setFilters(params) {
    this.innerHTML = '';

    // Helper to format major units (e.g., 500 => £500.00)
    const formatMoneyMajor = (amount) => {
      try {
        const currency = (window.Shopify && Shopify.currency && (Shopify.currency.active || Shopify.currency)) || undefined;
        const nf = new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'GBP' });
        return nf.format(Number(amount) || 0);
      } catch (_) {
        return '£' + (Number(amount) || 0).toFixed(2);
      }
    };

    // Build entries but skip empty values
    const entries = Array.from(params.entries()).filter(([name, value]) =>
      name.startsWith('filter.') && name !== 'sort_by' && value !== ''
    );

    // Price keys participate in visibility
    const minKey = 'filter.v.price.gte';
    const maxKey = 'filter.v.price.lte';
    const minVal = params.get(minKey);
    const maxVal = params.get(maxKey);

    const hasPrice = (minVal && String(minVal).trim() !== '') || (maxVal && String(maxVal).trim() !== '');
    if (entries.length === 0 && !hasPrice) {
      this.style.display = 'none';
      return;
    }

    this.style.display = '';
    if ((minVal && minVal !== '') || (maxVal && maxVal !== '')) {
      const tag = document.createElement('button');
      tag.className = `
        active-filter-tag 
        inline-flex items-center gap-2 px-3 py-2 pl-4 leading-none text-xs rounded-full bg-foreground text-black hover:bg-gray-200 transition-colors cursor-pointer
      `.trim();

      let text = '';
      if (minVal && maxVal) {
        text = `<span class="font-bold">Price</span>: ${formatMoneyMajor(minVal)} - ${formatMoneyMajor(maxVal)}`;
      } else if (minVal) {
        text = `<span class=\"font-bold\">Price</span>: ${formatMoneyMajor(minVal)} +`;
      } else if (maxVal) {
        text = `<span class=\"font-bold\">Price</span>: Up to ${formatMoneyMajor(maxVal)}`;
      }

      tag.innerHTML = `
        <span>${text}</span>
        <span class="ml-1 text-gray-500 text-sm leading-none">×</span>
      `;
      tag.setAttribute('data-filter-name', minKey);
      tag.setAttribute('data-alt-filter-name', maxKey);
      tag.setAttribute('aria-label', 'Remove price filter');

      tag.addEventListener('click', (e) => {
        e.preventDefault();
        // Clear URL params for price and trigger filters update
        const params = new URLSearchParams(window.location.search);
        params.delete(minKey);
        params.delete(maxKey);
        const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
        history.replaceState({}, '', newUrl);

        // Also clear any present inputs to keep UI in sync
        document.querySelectorAll(`[name="${minKey}"]`).forEach((el) => { el.value = ''; });
        document.querySelectorAll(`[name="${maxKey}"]`).forEach((el) => { el.value = ''; });

        // Notify listeners (FacetFilters listens to popstate to restore & fetch)
        window.dispatchEvent(new PopStateEvent('popstate'));
      });

      this.appendChild(tag);
    }

    // Render other non-price filters as individual tags (with group label)
    entries.forEach(([name, value]) => {
      if (name === minKey || name === maxKey) return; // handled above
      const tag = document.createElement('button');
      tag.className = `
        active-filter-tag 
        inline-flex items-center gap-2 px-3 py-2 pl-4 leading-none text-xs rounded-full bg-foreground text-black hover:bg-gray-200 transition-colors cursor-pointer
      `.trim();
    
      const label = document.createElement('span');
      // Try to find a human-friendly label from the form
      const input = document.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`);
      let valueLabel = input ? (input.closest('label')?.textContent || '').trim() : '';
      // Allow inputs to override chip text via data attributes (e.g., for toggles)
      if (input && input.dataset && input.dataset.chipLabel) {
        valueLabel = input.dataset.chipLabel;
      }
      let groupLabel = '';
      if (input) {
        const details = input.closest('details');
        const summarySpan = details ? details.querySelector('summary span') : null;
        groupLabel = (summarySpan?.textContent || '').trim();
        if (!groupLabel && input.dataset && input.dataset.groupLabel) {
          groupLabel = input.dataset.groupLabel;
        }
      }
      const finalText = `${groupLabel ? groupLabel + ': ' : ''}${valueLabel || value}`;
      if (groupLabel) {
        const strong = document.createElement('span');
        strong.className = 'font-bold';
        strong.textContent = groupLabel + ':';
        label.append(strong, ' ', valueLabel || value);
      } else {
        label.textContent = valueLabel || value;
      }
    
      const removeIcon = document.createElement('span');
      removeIcon.textContent = '×';
      removeIcon.className = `
        ml-1 
        text-gray-500 
        text-sm
        leading-none
      `.trim();
    
      tag.setAttribute('data-name', name);
      tag.setAttribute('data-value', value);
      tag.setAttribute('aria-label', `Remove ${finalText}`);
    
      tag.append(label, removeIcon);
      this.appendChild(tag);
    });
    
    // Add a "Clear all" control when there are any active entries
    if (entries.length > 0 || (minVal && minVal !== '') || (maxVal && maxVal !== '')) {
      const clearAll = document.createElement('button');
      clearAll.type = 'button';
      clearAll.textContent = 'Clear all';
      clearAll.className = 'ml-2 inline-flex items-center text-xs underline text-gray-700 hover:text-black';
      clearAll.addEventListener('click', (e) => {
        e.preventDefault();
        const current = new URLSearchParams(window.location.search);
        // Preserve context params
        const q = current.get('q');
        const type = current.get('type');
        const sortBy = current.get('sort_by');
        // Remove all filter.* keys
        Array.from(current.keys()).forEach((key) => {
          if (key.startsWith('filter.')) current.delete(key);
          if (key === 'page') current.delete(key);
        });
        if (q) current.set('q', q);
        if (type) current.set('type', type);
        if (sortBy) current.set('sort_by', sortBy);

        const newUrl = current.toString() ? `${window.location.pathname}?${current}` : window.location.pathname;
        history.replaceState({}, '', newUrl);
        // Let FacetFiltersFormComponent react via popstate
        window.dispatchEvent(new PopStateEvent('popstate'));
        
        // Force uncheck all filter checkboxes and update visual styles AFTER popstate
        setTimeout(() => {
          document.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((checkbox) => {
            checkbox.checked = false;
            
            // Update visual checkbox styles (custom checkbox implementation)
            const label = checkbox.closest('label');
            if (label) {
              const visualCheckbox = label.querySelector('span.h-4.w-4');
              if (visualCheckbox) {
                visualCheckbox.classList.remove('bg-input-text', 'border-input-text');
                visualCheckbox.classList.add('border-input-border', 'bg-white');
                
                const checkmark = visualCheckbox.querySelector('svg');
                if (checkmark) {
                  checkmark.classList.remove('opacity-100');
                  checkmark.classList.add('opacity-0');
                }
              }
            }
          });
          
          document.querySelectorAll('input[name*="filter.v.price"]').forEach((input) => {
            input.value = '';
          });
        }, 10);
      });
      const wrapper = document.createElement('div');
      wrapper.className = 'inline-flex items-center';
      wrapper.appendChild(clearAll);
      this.appendChild(wrapper);
    }
    
  }

  getRemoveButtons() {
    return this.querySelectorAll('.active-filter-tag');
  }
}

// Register early
if (!customElements.get('active-filters')) {
  customElements.define('active-filters', ActiveFiltersComponent);
}

class FacetFiltersFormComponent extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.activeFiltersEls = Array.from(document.querySelectorAll('active-filters'));

    if (!this.form) {
      // Form may not be parsed yet; watch for it
      const mo = new MutationObserver(() => {
        this.form = this.querySelector('form');
        if (this.form) {
          mo.disconnect();
          this.#initialize();
        }
      });
      mo.observe(this, { childList: true, subtree: true });
      return;
    }
  
    // Form exists, initialize immediately
    this.#initialize();
  }
  
  #initialize() {
    if (this._initialized) return; // Prevent double initialization
    this._initialized = true;
    
    // 1. Bind event listeners
    this.#bindInitialEventListeners();
    
    // 2. Restore filter UI from URL
    this.#restoreFiltersFromUrl();
    this.#bindDetailsAccordionBehavior();
  
    // 3. Render active filters on initial load
    const params = new URLSearchParams(window.location.search);
    this.#ensureActiveFiltersReady().then((ready) => {
      if (window.__skipActiveFiltersSetOnce) {
        window.__skipActiveFiltersSetOnce = false;
        return;
      }
      if (ready) {
        this.#setActiveFilters(params);
        this.#bindRemoveEvents();
      }
    });
  }
  

  #bindRemoveEvents() {
    this.#ensureActiveFiltersReady().then((ready) => {
      if (!ready) return;

      const buttons = Array.from(document.querySelectorAll('active-filters')).flatMap(el => Array.from(el.getRemoveButtons?.() || []));
      buttons.forEach(btn => {
        btn.addEventListener('click', (e) => {
        e.preventDefault();
        const name = btn.getAttribute('data-name');
        const value = btn.getAttribute('data-value');

        if (!name) return;

        // Try to uncheck the corresponding checkbox in the form first
        let checkbox;
        try {
          const selector = `[name="${CSS.escape(name)}"][value="${CSS.escape(value || '')}"]`;
          checkbox = this.form.querySelector(selector);
        } catch (_) {
          checkbox = null;
        }

        if (checkbox) {
          checkbox.checked = false;
          // Dispatch change event to trigger form's change listener (avoids duplicate calls)
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        // Fallback: manipulate URL params directly (handles cases where input isn't present)
        const params = new URLSearchParams(window.location.search);
        const existing = params.getAll(name);
        if (existing.length > 0) {
          params.delete(name);
          existing.forEach(v => {
            if (value == null || value === '' || v !== value) params.append(name, v);
          });
        }
        // Preserve search context
        const q = params.get('q');
        const type = params.get('type');
        if (q) params.set('q', q);
        if (type) params.set('type', type);

        history.replaceState({}, '', `${window.location.pathname}?${params}`);
        this.#fetchFilteredGrid(params);
        this.#setActiveFilters(params);
        this.#restoreFiltersFromUrl();
        });
      });
    });
  }

  /**
   * Public method to trigger filter change (can be called externally)
   */
  triggerFilterChange() {
    this.#handleFilterChange();
  }

  /**
   * Handles filter form changes by updating URL and fetching new results
   */
  #handleFilterChange() {
    // Exclude empty price fields from FormData by temporarily disabling them
    const tempDisabled = [];
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      const input = this.form.querySelector(`[name="${key}"]`);
      if (input && (input.value == null || String(input.value).trim() === '')) {
        if (!input.disabled) {
          input.disabled = true;
          tempDisabled.push(input);
        }
      }
    });

    const formData = new FormData(this.form);

    // Restore inputs after serialization
    tempDisabled.forEach((el) => { el.disabled = false; });
    
    // Collect sorting data - check both mobile (inside form) and desktop (outside form)
    if (!formData.get('sort_by')) {
      // Desktop sort is outside the form, so we need to find it separately
      // Look for any sort select/radio that's NOT the mobile one and NOT inside the mobile drawer
      const allSortSelects = document.querySelectorAll('select[name="sort_by"]');
      const desktopSort = Array.from(allSortSelects).find(select => {
        return select.id !== 'sort-by-mobile' && !select.closest('mobile-filter-drawer-content');
      });
      if (desktopSort && desktopSort.value) {
        formData.set('sort_by', desktopSort.value);
      } else {
        // Check for radio buttons
        const sortRadio = document.querySelector('input[type="radio"][name="sort_by"]:checked');
        if (sortRadio && sortRadio.value) {
          formData.set('sort_by', sortRadio.value);
        }
      }
    }
    
    const params = new URLSearchParams(formData);
    ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplying, { detail: { params: params.toString() } }));

    // Preserve search query context (search page) so filters don't drop the query
    const existingParams = new URLSearchParams(window.location.search);
    
    // Check if this is sorting-only (no filter changes)
    const hasFilters = Array.from(params.keys()).some(key => key.startsWith('filter.'));
    const hadFilters = Array.from(existingParams.keys()).some(key => key.startsWith('filter.'));
    const isSortingOnly = !hasFilters && !hadFilters && params.has('sort_by');
    
    // Disable image fade animation for sorting-only changes
    if (isSortingOnly) {
      window.__disableImageFadeOnce = true;
    }
    const q = existingParams.get('q');
    const type = existingParams.get('type');
    if (q) params.set('q', q);
    if (type) params.set('type', type);

    // Normalize price params: drop keys when inputs are blank or invalid
    ['filter.v.price.gte', 'filter.v.price.lte'].forEach((key) => {
      const raw = params.get(key);
      if (raw == null) return;
      const val = String(raw).trim();
      // Drop empty, non-number, or zero values (treat 0 as unset)
      if (val === '' || Number.isNaN(Number(val)) || Number(val) === 0) {
        params.delete(key);
      }
    });
    
    history.replaceState({}, '', `${window.location.pathname}?${params}`);
    ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplied, { detail: { params: params.toString(), status: 'optimistic' } }));
    // Update chips immediately, then fetch grid and re-bind remove listeners
    this.#setActiveFilters(params);
    this.#fetchFilteredGrid(params);
    this.#bindRemoveEvents();
  }

  /**
   * Gets the dynamic section ID from the shopify-section wrapper element
   * @returns {string} The section ID without the 'section-' prefix
   */
  #getSectionId() {
    // Find the shopify-section element that wraps this filter form
    let sectionElement = this.closest('.shopify-section') || this.form?.closest('.shopify-section');
    
    // If not found, look for the main collection section specifically
    if (!sectionElement) {
      // Look for the main section (contains the product grid)
      sectionElement = document.querySelector('#ProductGridContainer')?.closest('.shopify-section') || 
                     document.querySelector('[id*="__main"]') ||
                     document.querySelector('.shopify-section');
    }
    
    if (sectionElement && sectionElement.id) {
      // Strip the 'section-' prefix from the ID
      // e.g., 'shopify-section-template--26025869312339__main' becomes 'template--26025869312339__main'
      const sectionId = sectionElement.id.replace(/^shopify-section-/, '');
      return sectionId;
    }
    
    // Fallback to 'main' if no section ID found (since collection template uses 'main' section)
    return 'main';
  }

  // Update all active-filters instances with current params
  #setActiveFilters(params) {
    const els = Array.from(document.querySelectorAll('active-filters'));
    els.forEach((el) => {
      try { el.setFilters?.(params); } catch (_) {}
    });
  }

  /**
   * Fetches filtered product grid via AJAX and updates the DOM
   * @param {URLSearchParams} params - The filter parameters
   */
  #fetchFilteredGrid(params) {
    const sectionId = this.#getSectionId();
    const url = `${window.location.pathname}?section_id=${sectionId}&${params}`;
    
    // Prevent duplicate fetches from multiple form instances
    window.__fetchInProgress = true;
    
    // Prevent stale updates by tracking the most recent request
    this._lastFetchToken = (this._lastFetchToken || 0) + 1;
    const currentToken = this._lastFetchToken;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch filtered results: ${res.status} ${res.statusText}`);
        return res.text();
      })
      .then((html) => {
        // Skip if a newer request finished
        if (currentToken !== this._lastFetchToken) return;

        const newDom = new DOMParser().parseFromString(html, 'text/html');
        const newGrid = newDom.querySelector('#ProductGridContainer');
        const currentGrid = document.querySelector('#ProductGridContainer');
        
        if (currentGrid) {
          if (newGrid) {
            currentGrid.innerHTML = newGrid.innerHTML;
            
            // Recalculate stagger delays and reset animations
            if (window.ScrollAnimations) {
              if (typeof window.ScrollAnimations.setupStaggerContainers === 'function') {
                window.ScrollAnimations.setupStaggerContainers();
              }
              // Remove animate-in class to retrigger animations
              requestAnimationFrame(() => {
                const newElements = currentGrid.querySelectorAll('[data-animate]');
                newElements.forEach(el => el.classList.remove('animate-in'));
              });
            }
            
            // Update filter states (disable unavailable options)
            this.#updateFilterStates(newDom);
            
            // Dispatch gridUpdated event to trigger lazy loading (centralized in lazy-image.js)
            ThemeBus.dispatchEvent(new Event(EVENTS.gridUpdated));
          } else {
            // No grid returned (e.g., no results) → render empty state if template present
            const emptyTpl = document.getElementById('FiltersEmptyTemplate');
            currentGrid.innerHTML = emptyTpl ? emptyTpl.innerHTML : '';
            // Wire up Clear Filters link
            const clearBtn = document.getElementById('FiltersEmptyClear');
            if (clearBtn) {
              clearBtn.addEventListener('click', (e) => {
                e.preventDefault();
                const current = new URLSearchParams(window.location.search);
                const q = current.get('q');
                const type = current.get('type');
                const sortBy = current.get('sort_by');
                Array.from(current.keys()).forEach((key) => {
                  if (key.startsWith('filter.')) current.delete(key);
                  if (key === 'page') current.delete(key);
                });
                if (q) current.set('q', q);
                if (type) current.set('type', type);
                if (sortBy) current.set('sort_by', sortBy);
                const newUrl = current.toString() ? `${window.location.pathname}?${current}` : window.location.pathname;
                history.replaceState({}, '', newUrl);
                window.dispatchEvent(new PopStateEvent('popstate'));
                
                // Force uncheck all filter checkboxes and update visual styles
                setTimeout(() => {
                  document.querySelectorAll('input[type="checkbox"][name^="filter."]').forEach((checkbox) => {
                    checkbox.checked = false;
                    
                    const label = checkbox.closest('label');
                    if (label) {
                      const visualCheckbox = label.querySelector('span.h-4.w-4');
                      if (visualCheckbox) {
                        visualCheckbox.classList.remove('bg-input-text', 'border-input-text');
                        visualCheckbox.classList.add('border-input-border', 'bg-white');
                        
                        const checkmark = visualCheckbox.querySelector('svg');
                        if (checkmark) {
                          checkmark.classList.remove('opacity-100');
                          checkmark.classList.add('opacity-0');
                        }
                      }
                    }
                  });
                  
                  document.querySelectorAll('input[name*="filter.v.price"]').forEach((input) => {
                    input.value = '';
                  });
                }, 10);
              });
            }
            ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersNoResults, { detail: { params: params.toString() } }));
          }

          // Update product count (if present)
          this.#updateProductCount(newDom);

          // Notify other components via ThemeBus
          ThemeBus.dispatchEvent(new Event(EVENTS.filtersUpdated));
          ThemeBus.dispatchEvent(new CustomEvent(EVENTS.filtersApplied, { detail: { params: params.toString(), status: 'done' } }));
        }
        
        // Clear fetch lock
        window.__fetchInProgress = false;
      })
      .catch((error) => {
        console.error('Filter fetch failed:', error);
        // Clear fetch lock on error too
        window.__fetchInProgress = false;
      });
  }

  /**
   * Updates the product count display with the new count from AJAX response
   * @param {Document} newDom - The new DOM from the AJAX response
   */
  #updateProductCount(newDom) {
    const newProductCount = newDom.querySelector('[data-product-count]');
    const currentProductCounts = document.querySelectorAll('[data-product-count]');
    
    if (newProductCount && currentProductCounts.length > 0) {
      // Update all product count elements on the page
      currentProductCounts.forEach((currentProductCount, index) => {
        currentProductCount.innerHTML = newProductCount.innerHTML;
      });
    }
  }

  /**
   * Updates filter input states (enable/disable) based on available products
   * @param {Document} newDom - The new DOM from the AJAX response
   */
  #updateFilterStates(newDom) {
    // Get all filter inputs from the AJAX response
    const newFilterInputs = newDom.querySelectorAll('[data-filter-count]');
    
    newFilterInputs.forEach(newInput => {
      const filterName = newInput.getAttribute('name');
      const filterValue = newInput.getAttribute('value');
      const filterCount = parseInt(newInput.getAttribute('data-filter-count') || '0');
      const isActive = newInput.checked;
      
      // Find the corresponding input on the current page
      // Need to check both desktop and mobile forms
      const currentInputs = document.querySelectorAll(`[name="${CSS.escape(filterName)}"][value="${CSS.escape(filterValue)}"]`);
      
      currentInputs.forEach(currentInput => {
        const currentCount = parseInt(currentInput.getAttribute('data-filter-count') || '0');
        
        // Only update if count has changed
        if (currentCount !== filterCount) {
          // Update the count attribute
          currentInput.setAttribute('data-filter-count', filterCount);
          
          // Update disabled state (but keep active filters enabled so they can be unchecked)
          const shouldDisable = filterCount === 0 && !currentInput.checked;
          currentInput.disabled = shouldDisable;
          
          // Update the label styling
          const label = currentInput.closest('label');
          if (label) {
            if (shouldDisable) {
              label.classList.add('opacity-50', 'cursor-not-allowed');
              label.classList.remove('cursor-pointer');
            } else {
              label.classList.remove('opacity-50', 'cursor-not-allowed');
              label.classList.add('cursor-pointer');
            }
            
            // Update the count display
            const countSpan = label.querySelector('.text-gray-500');
            if (countSpan) {
              countSpan.textContent = `(${filterCount})`;
            }
          }
        }
      });
    });
  }

  /**
   * Restores filter checkboxes and select elements based on current URL parameters
   */
  #restoreFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    // Uncheck all checkboxes first
    this.form.querySelectorAll("input[type=checkbox]").forEach((el) => el.checked = false);

    for (const [name, value] of params.entries()) {
      // Handle checkboxes
      const input = this.form.querySelector(`[name="${name}"][value="${value}"]`);
      if (input) input.checked = true;
      
      // Handle sort_by select - update all sort selects (both desktop and mobile)
      if (name === 'sort_by') {
        const allSortSelects = document.querySelectorAll('select[name="sort_by"]');
        allSortSelects.forEach(select => {
          if (select) select.value = value;
        });
      }
    }
  }

  /**
   * Bind initial event listeners for filter form, sort select, and history
   */
  #bindInitialEventListeners() {
    if (!this.form) return;
    
    // Bind form change listener
    this._formChangeHandler = (event) => {
      // Don't trigger immediate filter changes for mobile filter drawer
      // The mobile drawer will apply filters when "Apply Filters" button is clicked
      if (this.form.id === 'mobile-filter-form') {
        return; // Skip immediate application for mobile drawer
      }
      this.#handleFilterChange();
    };
    this.form.addEventListener("change", this._formChangeHandler);

    // Bind popstate listener (only once per instance)
    if (!this._popstateHandler) {
      this._popstateHandler = () => {
        this.#restoreFiltersFromUrl();
        
        // Only fetch if URL has actual filters/sort (not just page param)
        const params = new URLSearchParams(window.location.search);
        const hasFiltersOrSort = Array.from(params.keys()).some(key => 
          key.startsWith('filter.') || key === 'sort_by' || key === 'q' || key === 'type'
        );
        
        // Prevent duplicate fetches from multiple form instances
        // Mark this instance as the one handling the fetch
        if (hasFiltersOrSort && !window.__fetchInProgress) {
          window.__fetchInProgress = true;
          this.#fetchFilteredGrid(params);
        } else {
          // This instance won't fetch, but should still update UI
          this.#setActiveFilters(params);
          this.#bindRemoveEvents();
        }
      };
      window.addEventListener("popstate", this._popstateHandler);
    }
  }

  // Close sibling filter popovers when one opens (horizontal UI only, not mobile drawer)
  #bindDetailsAccordionBehavior() {
    if (!this.form) return;
    
    // Skip accordion behavior for mobile filter drawer - allow multiple accordions open
    if (this.form.id === 'mobile-filter-form') {
      return;
    }
    
    const container = this.form.querySelector('filter-set') || this.form;
    const detailsList = container.querySelectorAll('details');
    if (!detailsList.length) return;
    detailsList.forEach((el) => {
      el.addEventListener('toggle', () => {
        if (!el.open) return;
        detailsList.forEach((other) => {
          if (other !== el) other.open = false;
        });
      });
    });
  }

  /**
   * Ensures the <active-filters> custom element is present and upgraded
   * @returns {Promise<boolean>} true if ready
   */
  async #ensureActiveFiltersReady() {
    if (!this.activeFiltersEl) {
      this.activeFiltersEl = document.querySelector('active-filters');
    }
    if (!this.activeFiltersEl) return false;
    // Try immediate upgrade if the definition exists
    try {
      if (customElements.get('active-filters')) {
        customElements.upgrade?.(this.activeFiltersEl);
      }
    } catch (_) {}
    if (typeof this.activeFiltersEl.setFilters === 'function') return true;
    // Wait a microtask and a frame to allow upgrade after DOM replacement
    await Promise.resolve();
    await new Promise((r) => requestAnimationFrame(r));
    try {
      if (customElements.get('active-filters')) {
        customElements.upgrade?.(this.activeFiltersEl);
      } else {
        await customElements.whenDefined('active-filters');
        customElements.upgrade?.(this.activeFiltersEl);
      }
    } catch (_) {}
    // Re-select in case the element reference changed after swap
    this.activeFiltersEl = document.querySelector('active-filters');
    return !!(this.activeFiltersEl && typeof this.activeFiltersEl.setFilters === 'function');
  }

}

export async function initFilters() {
  if (!customElements.get("facet-filters-form")) {
    customElements.define("facet-filters-form", FacetFiltersFormComponent);

    // Attach a single global listener for sort changes (outside the form)
    // This prevents multiple listeners from firing when there are multiple facet-filters-form elements
    document.addEventListener("change", (event) => {
      // Handle sort radio buttons
      if (event.target.matches && (event.target.matches('select[name="sort_by"]') || event.target.matches('input[name="sort_by"]'))) {
        // Check if this is mobile or desktop
        const isMobile = event.target.closest('mobile-filter-drawer-content');
        
        if (!isMobile) {
          // Desktop sort - check if it's inside a form or standalone
          const sortForm = event.target.closest('form');
          
          if (sortForm) {
            // If inside a form, trigger the form's filter change
            const formElement = sortForm.closest('facet-filters-form');
            if (formElement && formElement.triggerFilterChange) {
              formElement.triggerFilterChange();
            }
          } else {
            // Standalone desktop sort (not in a form) - manually update URL
            const sortValue = event.target.value;
            const params = new URLSearchParams(window.location.search);
            params.set('sort_by', sortValue);
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            
            // Update URL and trigger page update
            history.replaceState({}, '', newUrl);
            window.dispatchEvent(new PopStateEvent('popstate'));
          }
        }
        
        // Update the sort label text and visual checkbox state (for both desktop and mobile)
        if (event.target.type === 'radio' && event.target.checked) {
          const label = event.target.closest('label');
          if (label) {
            // Update dropdown summary text (desktop only - has details element)
            const sortText = label.querySelector('span.text-primary')?.textContent;
            const summary = event.target.closest('details')?.querySelector('summary > span');
            if (summary && sortText) {
              summary.textContent = sortText;
            }
            
            // Update visual checkbox states (fake checkboxes) - works for both details and filter-accordion
            const sortContainer = event.target.closest('details, filter-accordion');
            if (sortContainer) {
              // Remove checked state from all sort options
              sortContainer.querySelectorAll('input[name="sort_by"]').forEach(radio => {
                const visualCheckbox = radio.nextElementSibling;
                if (visualCheckbox && visualCheckbox.tagName === 'SPAN') {
                  visualCheckbox.classList.remove('bg-input-text', 'border-input-text');
                  visualCheckbox.classList.add('bg-white', 'border-input-border');
                  const svg = visualCheckbox.querySelector('svg');
                  if (svg) svg.classList.add('opacity-0');
                  if (svg) svg.classList.remove('opacity-100');
                }
              });
              
              // Add checked state to selected option
              const visualCheckbox = event.target.nextElementSibling;
              if (visualCheckbox && visualCheckbox.tagName === 'SPAN') {
                visualCheckbox.classList.add('bg-input-text', 'border-input-text');
                visualCheckbox.classList.remove('bg-white', 'border-input-border');
                const svg = visualCheckbox.querySelector('svg');
                if (svg) svg.classList.remove('opacity-0');
                if (svg) svg.classList.add('opacity-100');
              }
            }
          }
        }
      }
      
      // Handle filter checkboxes (update visual fake checkbox state)
      if (event.target.matches && event.target.matches('input[type="checkbox"]') && event.target.classList.contains('sr-only')) {
        const visualCheckbox = event.target.nextElementSibling;
        if (visualCheckbox && visualCheckbox.tagName === 'SPAN' && visualCheckbox.querySelector('svg')) {
          // This is a fake checkbox - update its visual state
          if (event.target.checked) {
            visualCheckbox.classList.add('bg-input-text', 'border-input-text');
            visualCheckbox.classList.remove('bg-white', 'border-input-border');
            const svg = visualCheckbox.querySelector('svg');
            if (svg) {
              svg.classList.remove('opacity-0');
              svg.classList.add('opacity-100');
            }
          } else {
            visualCheckbox.classList.remove('bg-input-text', 'border-input-text');
            visualCheckbox.classList.add('bg-white', 'border-input-border');
            const svg = visualCheckbox.querySelector('svg');
            if (svg) {
              svg.classList.add('opacity-0');
              svg.classList.remove('opacity-100');
            }
          }
        }
      }
    });
  }
  
  // Initialize filter accordions
  initFilterAccordions();
}

// Initialize filter accordions with button-based behavior
function initFilterAccordions() {
  const accordions = document.querySelectorAll('[data-filter-accordion]');
  
  accordions.forEach(accordion => {
    const trigger = accordion.querySelector('[data-accordion-trigger]');
    const content = accordion.querySelector('[data-accordion-content]');
    const icon = accordion.querySelector('[data-accordion-icon]');
    
    if (!trigger || !content) return;
    
    // Skip if already initialized
    if (trigger.dataset.accordionInitialized) return;
    trigger.dataset.accordionInitialized = 'true';
    
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        // Close - measure current height first, then transition to 0
        const currentHeight = content.scrollHeight;
        content.style.height = currentHeight + 'px';
        
        // Force reflow
        content.offsetHeight;
        
        // Now transition to 0
        requestAnimationFrame(() => {
          trigger.setAttribute('aria-expanded', 'false');
          content.style.height = '0';
          content.classList.add('overflow-hidden');
          content.classList.remove('mt-3');
          if (icon) icon.classList.remove('rotate-180');
        });
      } else {
        // Open - start at 0, then transition to full height
        content.classList.add('overflow-hidden');
        content.classList.remove('mt-3');
        trigger.setAttribute('aria-expanded', 'true');
        
        // Force a layout recalculation to get accurate scrollHeight
        content.style.display = 'block';
        
        // Get the full height
        const fullHeight = content.scrollHeight;
        
        // Set to 0 first
        content.style.height = '0';
        content.style.display = '';
        
        // Force reflow
        content.offsetHeight;
        
        // Animate to full height
        requestAnimationFrame(() => {
          content.style.height = fullHeight + 'px';
          if (icon) icon.classList.add('rotate-180');
        });
        
        // After transition, set to auto so it can resize naturally
        const transitionEndHandler = () => {
          content.style.height = 'auto';
          content.classList.remove('overflow-hidden');
          content.classList.add('mt-3');
          content.removeEventListener('transitionend', transitionEndHandler);
        };
        content.addEventListener('transitionend', transitionEndHandler);
      }
    });
  });
}

// Auto-initialize when module loads
initFilters();
